import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown, Copy, Globe, MapPin, Users } from 'lucide-react'
import type { Deling, Prioriteit, TicketStatus, Vestiging } from '../../gedeeld/types'
import { prioriteit, rolLabel, ticketStatus } from '../lib/woorden'
import { meld, mensNaam, vestigingNaam } from '../lib/staat'
import { Link } from '../lib/router'
import { MenuRegel, Uitklap } from './ui'

/* ===========================================================================
 *  Stukjes die op meer dan één scherm van IT terugkomen
 * =========================================================================== */

export function StatusPil({ status }: { status: TicketStatus }) {
  const s = ticketStatus(status)
  return <span className={`status-pil t-${s.kleur}`}>{s.label}</span>
}

export function PrioPil({ p, alleenAfwijkend }: { p: Prioriteit; alleenAfwijkend?: boolean }) {
  if (alleenAfwijkend && p === 'normaal') return null
  const x = prioriteit(p)
  return <span className={`prio t-${x.kleur}`} title={x.uitleg}>{x.label}</span>
}

/** Een tekst kopiëren, met een vinkje als het gelukt is. */
export function Kopieer({ tekst, label = 'Kopiëren', klein = true, onGekopieerd }: {
  tekst: string | (() => Promise<string>)
  label?: string
  klein?: boolean
  onGekopieerd?: () => void
}) {
  const [klaar, setKlaar] = useState(false)
  async function kopieer() {
    try {
      const t = typeof tekst === 'string' ? tekst : await tekst()
      await navigator.clipboard.writeText(t)
      setKlaar(true)
      onGekopieerd?.()
      setTimeout(() => setKlaar(false), 1600)
    } catch (e) {
      meld(e instanceof Error ? e.message : 'Kopiëren lukte niet.', 'fout')
    }
  }
  return (
    <button type="button" className={`knop k-stil ${klein ? 'klein' : ''} alleen-icoon`} onClick={() => void kopieer()}
      title={label} aria-label={label}>
      {klaar ? <Check size={14} className="kleur-ok" /> : <Copy size={14} />}
    </button>
  )
}

/** Een cijfertegel, zoals op het overzicht van Projects. */
export function Tegel({ label, getal, sub, icoon, kleur = 'info', naar, gevaar }: {
  label: string; getal: number | string; sub?: string; icoon: ReactNode; kleur?: string; naar?: string; gevaar?: boolean
}) {
  const binnen = (
    <>
      <span className="tegel-label">{label}</span>
      <span className={`tegel-getal ${gevaar ? 'gevaar' : ''}`}>{getal}</span>
      {sub && <span className="tegel-sub">{sub}</span>}
      <span className={`tegel-glyph t-${kleur}`}>{icoon}</span>
    </>
  )
  return naar ? <Link naar={naar} className="tegel">{binnen}</Link> : <div className="tegel">{binnen}</div>
}

/** Een label en een waarde onder elkaar, voor de zijkant van een detailscherm. */
export function Eigenschap({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="eigenschap">
      <span className="eigenschap-label">{label}</span>
      <div className="eigenschap-waarde">{children}</div>
    </div>
  )
}

/** Een vestiging kiezen. Leeg = geen (van het hele bedrijf). */
export function KiesVestiging({ waarde, vestigingen, onKies, leeg = 'Geen vestiging', klein }: {
  waarde: string | null; vestigingen: Vestiging[]; onKies: (id: string | null) => void; leeg?: string; klein?: boolean
}) {
  const [open, setOpen] = useState(false)
  const nu = vestigingen.find((v) => v.id === waarde)
  return (
    <Uitklap open={open} onOpen={setOpen} breed={240}
      knop={
        <button type="button" className={`kies ${klein ? 'klein' : ''}`}>
          <MapPin size={14} className="stil" />
          <span>{nu?.naam ?? leeg}</span>
          <ChevronDown size={13} className="kies-pijl" />
        </button>
      }>
      <div className="menu-lijst">
        <MenuRegel actief={!waarde} onClick={() => { onKies(null); setOpen(false) }}>{leeg}</MenuRegel>
        {vestigingen.map((v) => (
          <MenuRegel key={v.id} actief={v.id === waarde} onClick={() => { onKies(v.id); setOpen(false) }}>
            {v.naam}{v.plaats && v.plaats !== v.naam ? <span className="stil"> · {v.plaats}</span> : null}
          </MenuRegel>
        ))}
      </div>
    </Uitklap>
  )
}

/** Hoe één deling heet, in woorden. */
export function delingTekst(d: Deling): string {
  const delen = [
    d.persoonId ? (mensNaam(d.persoonId) ?? 'iemand') : null,
    d.rol ? rolLabel(d.rol) : null,
    d.vestigingId ? (d.persoonId || d.rol ? 'in ' : 'iedereen in ') + (vestigingNaam(d.vestigingId) ?? 'een vestiging') : null,
  ].filter(Boolean)
  return delen.length ? delen.join(' ') : 'iedereen'
}

/** "Alleen IT", "Iedereen", of de eerste twee en een teller. */
export function GedeeldMet({ delingen }: { delingen: Deling[] | undefined }) {
  if (!delingen) return null
  if (!delingen.length) return <span className="gedeeld stil" title="Alleen IT ziet dit">Alleen IT</span>
  const iedereen = delingen.some((d) => !d.persoonId && !d.rol && !d.vestigingId)
  if (iedereen) return <span className="gedeeld" title="Iedereen die hier werkt"><Globe size={12} /> Iedereen</span>
  const teksten = delingen.map(delingTekst)
  return (
    <span className="gedeeld" title={teksten.join('\n')}>
      <Users size={12} /> {teksten.slice(0, 2).join(', ')}{teksten.length > 2 ? ` +${teksten.length - 2}` : ''}
    </span>
  )
}

/** Een kop boven een blok op een detailscherm. */
export function Blok({ titel, icoon, rechts, children }: { titel: string; icoon?: ReactNode; rechts?: ReactNode; children: ReactNode }) {
  return (
    <section className="blok">
      <div className="blok-kop">
        <h3>{icoon}{titel}</h3>
        {rechts && <span className="blok-rechts">{rechts}</span>}
      </div>
      {children}
    </section>
  )
}

/** Een tekstvak dat met de inhoud meegroeit. */
export function Groeiveld({ waarde, onWaarde, placeholder, min = 3, autoFocus, onCtrlEnter }: {
  waarde: string; onWaarde: (w: string) => void; placeholder?: string; min?: number; autoFocus?: boolean; onCtrlEnter?: () => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight + 2, 480) + 'px'
  }, [waarde])
  return (
    <textarea ref={ref} className="invoer" rows={min} value={waarde} placeholder={placeholder} autoFocus={autoFocus}
      onChange={(e) => onWaarde(e.target.value)}
      onKeyDown={(e) => { if (onCtrlEnter && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onCtrlEnter() } }} />
  )
}
